import React, { useRef, useState } from 'react';
import axios from 'axios';
import {
  Camera,
  UploadSimple,
  MagicWand,
  X,
  CheckCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_LABELS = {
  auto: 'Automatisch',
  personalausweis: 'Personalausweis',
  reisepass: 'Reisepass',
  fuehrerschein: 'Führerschein'
};

const IdScanCard = ({ onExtracted, customerId }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const [docType, setDocType] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Die Datei darf maximal 10 MB groß sein.');
      return;
    }

    setPreview(URL.createObjectURL(file));
    setResult(null);
    setLoading(true);

    try {
      const form = new FormData();
      form.append('file', file);
      form.append('doc_type', docType);

      if (customerId) {
        form.append('customer_id', customerId);
      }

      const response = await axios.post(
        `${API}/api/id-scan`,
        form,
        { withCredentials: true }
      );

      const responseData = response.data;
      setResult(responseData);

      if (onExtracted) {
        onExtracted(
          responseData.data || {},
          responseData.photo,
          responseData.doc_type
        );
      }

      toast.success('Dokument erfolgreich erkannt');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Dokument konnte nicht ausgewertet werden'
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setResult(null);

    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  return (
    <div
      data-testid="id-scan-card"
      className="border border-dashed rounded-lg p-4 space-y-4"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold flex items-center gap-2">
            <MagicWand size={20} />
            Ausweis scannen (KI)
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Dokument fotografieren oder als Datei hochladen. Erkannte Daten
            werden automatisch ins Kundenformular übernommen.
          </p>
        </div>

        {(preview || result) && (
          <button
            type="button"
            onClick={reset}
            className="p-2 rounded-md hover:bg-muted"
            title="Zurücksetzen"
          >
            <X size={18} />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.entries(DOC_LABELS).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setDocType(value)}
            className={`px-3 py-2 rounded-md text-sm border ${
              docType === value
                ? 'bg-foreground text-background'
                : 'bg-background'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 border rounded-md"
        >
          <UploadSimple size={18} />
          Datei wählen
        </button>

        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 border rounded-md"
        >
          <Camera size={18} />
          Kamera
        </button>

        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>

      {loading && (
        <div
          id="id-scan-loading"
          className="flex items-center gap-2 text-sm"
        >
          <MagicWand className="animate-pulse" size={18} />
          Dokument wird mit KI ausgewertet …
        </div>
      )}

      {preview && !preview.toLowerCase().includes('.pdf') && (
        <img
          src={preview}
          alt="Dokument-Vorschau"
          className="max-h-48 rounded-md border object-contain"
        />
      )}

      {result && (
        <div
          id="id-scan-result"
          className="rounded-md border p-3 space-y-2 text-sm"
        >
          <div className="font-medium flex items-center gap-2">
            <CheckCircle size={18} />
            Dokument erkannt: {DOC_LABELS[result.doc_type] || result.doc_type}
          </div>

          {result.data?.first_name && (
            <div>
              Name: {result.data.first_name} {result.data.last_name || ''}
            </div>
          )}

          {result.data?.date_of_birth && (
            <div>Geburtsdatum: {result.data.date_of_birth}</div>
          )}

          {result.data?.nationality && (
            <div>Nationalität: {result.data.nationality}</div>
          )}

          {result.data?.id_number && (
            <div>Ausweisnummer: {result.data.id_number}</div>
          )}

          {result.data?.driver_license_number && (
            <div>
              Führerschein-Nr.: {result.data.driver_license_number}
            </div>
          )}

          {result.data?.license_classes?.length > 0 && (
            <div>
              Klassen: {result.data.license_classes.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IdScanCard;
